import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent } from 'react';
import { api } from '../../../lib/api';
import type { NotificationItem, StudioInvitation } from '../../../lib/types';
import { Modal } from '../../../ui/kit/components/overlays';
import { Alert, Button, IconButton, cx } from '../../../ui/kit/components/primitives';
import { SegmentedControl } from '../../../ui/kit/components/navigation';
import { Icon, type IconName } from '../../../ui/kit/components/icons';
import '../domain-panels.css';
import './notifications.css';

type Delivery = Awaited<ReturnType<typeof api.notificationDeliveries>>['deliveries'][number];
type Filter = 'all' | 'unread' | 'reviews' | 'invites';
type Kind = { group: 'review' | 'invite' | 'work' | 'update'; icon: IconName; label: string; tone: 'accent' | 'neutral' | 'success' | 'warning' };

const SYNC_MS = 30000;
const NOTICE_MS = 4000;
const FRAMES = 6;
const message = (reason: unknown) => reason instanceof Error ? reason.message : 'The request failed. Please try again.';
const stamp = (value?: string | null) => value && Number.isFinite(Date.parse(value)) ? new Date(value) : null;
const pad = (value: number) => String(value).padStart(2, '0');
const clockOf = (date: Date) => `${pad(date.getHours())}:${pad(date.getMinutes())}`;

function kindOf(item: NotificationItem): Kind {
  const type = typeof item.type === 'string' ? item.type : '';
  if (type.startsWith('review.comment')) return { group: 'review', icon: 'pencil', label: 'Note', tone: 'neutral' };
  if (type.startsWith('review.item') && type.includes('approv')) return { group: 'review', icon: 'check', label: 'Approved', tone: 'success' };
  if (type.startsWith('review.item')) return { group: 'review', icon: 'undo', label: 'Changes', tone: 'warning' };
  if (type === 'review.presentation.expiring') return { group: 'review', icon: 'clock', label: 'Expiring', tone: 'warning' };
  if (type.startsWith('review.')) return { group: 'review', icon: 'play', label: 'Review', tone: 'accent' };
  if (type.includes('invitation')) return { group: 'invite', icon: 'users', label: 'Invite', tone: 'accent' };
  if (type.startsWith('work.')) return { group: 'work', icon: 'person', label: 'Assigned', tone: 'neutral' };
  return { group: 'update', icon: 'bell', label: 'Update', tone: 'neutral' };
}

function relative(date: Date | null, now: number) {
  if (!date) return 'Time unavailable';
  const seconds = Math.max(0, Math.round((now - date.getTime()) / 1000));
  if (seconds < 45) return 'Just now';
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h ago`;
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ` · ${clockOf(date)}`;
}

function dayGroup(date: Date | null, now: number) {
  if (!date) return 'Earlier';
  const today = new Date(now); today.setHours(0, 0, 0, 0);
  const diff = Math.floor((today.getTime() - new Date(date).setHours(0, 0, 0, 0)) / 86400000);
  if (diff <= 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff < 7) return 'This week';
  return 'Earlier';
}

function presentationOf(item: NotificationItem) {
  const payload = item.payload as { presentationId?: unknown } | undefined;
  return typeof item.project_id === 'string' && typeof payload?.presentationId === 'string' ? { projectId: item.project_id, presentationId: payload.presentationId } : null;
}

function Timecode() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const timer = window.setInterval(() => setNow(new Date()), 1000 / 12); return () => window.clearInterval(timer); }, []);
  return <>{`${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}:${pad(Math.floor(now.getMilliseconds() / 1000 * 24))}`}</>;
}

function CaughtUpArt({ compact = false }: { compact?: boolean }) {
  const stage = useRef<HTMLDivElement>(null);
  const [take, setTake] = useState(1);
  const scrub = (event: PointerEvent<HTMLDivElement>) => {
    const target = stage.current;
    if (!target || event.pointerType === 'touch') return;
    const box = target.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - box.left) / box.width));
    const x = Math.min(268, Math.max(52, ratio * 320));
    const cell = Math.floor((x - 58) / 36);
    target.style.setProperty('--scrub-x', `${x.toFixed(1)}px`);
    if (cell >= 0 && cell < FRAMES && x - 58 - cell * 36 <= 30) target.dataset.frame = String(cell); else delete target.dataset.frame;
    target.classList.add('is-scrubbing');
  };
  const release = () => { stage.current?.classList.remove('is-scrubbing'); if (stage.current) delete stage.current.dataset.frame; };
  return (
    <div ref={stage} className={cx('notif-art-stage', compact && 'is-compact')} onPointerMove={scrub} onPointerLeave={release}>
      <svg className="notif-art" viewBox="0 0 320 176" aria-hidden="true" focusable="false">
        <defs><clipPath id="notif-art-picture"><rect x="58" y="30" width="204" height="84" rx="2" /></clipPath></defs>
        <rect x="46" y="14" width="228" height="112" rx="6" className="notif-art-surface" />
        <text x="58" y="25" className="notif-art-mono">DAILIES · REVIEW QUEUE</text>
        <text x="262" y="25" textAnchor="end" className="notif-art-mono">00 NOTES</text>
        <g clipPath="url(#notif-art-picture)">
          <rect x="58" y="30" width="204" height="84" className="notif-art-picture" />
          <circle cx="226" cy="54" r="10" className="notif-art-line notif-art-sun" />
          <path d="M58 104 L98 76 L124 92 L160 64 L206 98 L262 80" className="notif-art-line" />
          <path d="M58 114 L58 104 L98 76 L124 92 L160 64 L206 98 L262 80 L262 114 Z" className="notif-art-ground" />
          <rect x="58" y="30" width="204" height="8" className="notif-art-scan" />
        </g>
        <path d="M64 36 h8 M64 36 v8 M256 36 h-8 M256 36 v8 M64 108 h8 M64 108 v-8 M256 108 h-8 M256 108 v-8" className="notif-art-line notif-art-faint" />
        <g className="notif-art-badge">
          <rect x="118" y="61" width="84" height="22" rx="4" className="notif-art-badge-plate" />
          <circle cx="131" cy="72" r="2.5" className="notif-art-accent-fill notif-art-live" />
          <text x="166" y="75.5" textAnchor="middle" className="notif-art-mono notif-art-badge-text">ALL CLEAR</text>
        </g>
        <g className="notif-art-strip">
          {Array.from({ length: FRAMES }, (_, index) => (
            <g key={index} className="notif-art-cell" style={{ '--i': index } as CSSProperties} data-cell={index}>
              <rect x={58 + index * 36} y="140" width="30" height="22" rx="2" className="notif-art-frame" />
              <path d={`M${67 + index * 36} 151 l4 4 l8 -8`} className="notif-art-check" pathLength={1} />
            </g>
          ))}
          <path d="M52 136 H268 M52 166 H268" className="notif-art-line notif-art-faint" />
        </g>
        <g className="notif-art-playhead">
          <path d="M0 134 l-4 -6 h8 z" className="notif-art-accent-fill" />
          <rect x="-0.75" y="134" width="1.5" height="34" className="notif-art-accent-fill" />
        </g>
        <g className="notif-art-slate" key={take}>
          <rect x="6" y="92" width="56" height="42" rx="3" className="notif-art-slate-board" />
          <text x="12" y="108" className="notif-art-mono notif-art-slate-text">SC 00</text>
          <text x="12" y="120" className="notif-art-mono notif-art-slate-text">TK {pad(take)}</text>
          <text x="12" y="129" className="notif-art-mono notif-art-slate-faint">NO NOTES</text>
          <g className="notif-art-clapper">
            <rect x="6" y="82" width="56" height="9" rx="2" className="notif-art-slate-board" />
            <path d="M14 82 l-5 9 M26 82 l-5 9 M38 82 l-5 9 M50 82 l-5 9 M62 83 l-4 8" className="notif-art-line" />
          </g>
        </g>
      </svg>
      {!compact && <button type="button" className="notif-art-hit" aria-label="Slate a new take" onClick={() => setTake(value => value % 99 + 1)} />}
    </div>
  );
}

function SkeletonRows() {
  return <ul className="notif-list" aria-hidden="true">{[0, 1, 2].map(index => <li key={index} className="notif-skeleton" style={{ '--i': index } as CSSProperties}><span /><div><i /><i /></div></li>)}</ul>;
}

export default function Notifications({ onClose, onReview }: { onClose: () => void; onReview: (projectId: string, presentationId: string) => Promise<void> }) {
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [studioInvites, setStudioInvites] = useState<StudioInvitation[]>([]);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncedAt, setSyncedAt] = useState<Date | null>(null);
  const [error, setError] = useState('');
  const [deliveryError, setDeliveryError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');
  const [fresh, setFresh] = useState<Set<string>>(() => new Set());
  const [now, setNow] = useState(() => Date.now());
  const lock = useRef(false);
  const live = useRef(true);
  const known = useRef<Set<string> | null>(null);
  const inflight = useRef(false);
  const edits = useRef(0);

  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);

  const load = useCallback(async (silent: boolean) => {
    if (inflight.current) return;
    inflight.current = true;
    if (silent) setSyncing(true); else setLoading(true);
    const startedAt = edits.current;
    const [updates, history, studios] = await Promise.allSettled([api.notifications(), api.notificationDeliveries(), api.studios()]);
    inflight.current = false;
    if (!live.current) return;
    if (updates.status === 'fulfilled' && silent && startedAt !== edits.current) setSyncedAt(new Date());
    else if (updates.status === 'fulfilled') {
      const next = updates.value.notifications;
      if (known.current) {
        const arrived = next.filter(item => !known.current?.has(item.id)).map(item => item.id);
        if (arrived.length) setFresh(current => new Set([...current, ...arrived]));
      }
      known.current = new Set(next.map(item => item.id));
      setItems(next); setError(''); setSyncedAt(new Date());
    } else if (!silent) setError(message(updates.reason));
    if (studios.status === 'fulfilled') setStudioInvites(Array.isArray(studios.value?.invitations) ? studios.value.invitations : []);
    if (history.status === 'fulfilled') { setDeliveries(history.value.deliveries); setDeliveryError(''); }
    else if (!silent) setDeliveryError(message(history.reason));
    setLoading(false); setSyncing(false); setNow(Date.now());
  }, []);

  useEffect(() => {
    void load(false);
    const timer = window.setInterval(() => { if (document.visibilityState === 'visible') void load(true); }, SYNC_MS);
    const wake = () => { if (document.visibilityState === 'visible') void load(true); };
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('focus', wake);
    return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', wake); window.removeEventListener('focus', wake); };
  }, [load]);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(''), NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [notice]);

  async function run(action: () => Promise<void>) {
    if (lock.current) return;
    lock.current = true; setBusy(true); setError(''); setNotice('');
    try { await action(); }
    catch (reason) { if (live.current) setError(message(reason)); }
    finally { lock.current = false; if (live.current) setBusy(false); }
  }

  const markLocal = (id: string, patch: Partial<NotificationItem> = {}) => { edits.current += 1; setItems(current => current.map(value => value.id === id ? { ...value, read_at: value.read_at || new Date().toISOString(), ...patch } : value)); };

  async function markRead(item: NotificationItem) {
    if (!item.read_at) await api.readNotification(item.id);
    if (live.current) markLocal(item.id);
  }

  async function open(item: NotificationItem) {
    await markRead(item);
    const target = presentationOf(item);
    if (target && live.current) await onReview(target.projectId, target.presentationId);
  }

  async function respond(item: NotificationItem, accept: boolean) {
    const payload = item.payload as { invitationId?: unknown } | undefined;
    const invitationId = typeof payload?.invitationId === 'string' ? payload.invitationId : '';
    if (!invitationId) return;
    await (accept ? api.acceptProjectInvitation(invitationId) : api.declineProjectInvitation(invitationId));
    if (!live.current) return;
    markLocal(item.id, { payload: { ...(item.payload as Record<string, unknown> || {}), responded: accept ? 'accepted' : 'declined' } });
    setNotice(accept ? 'Invitation accepted.' : 'Invitation declined.');
  }

  async function answerStudio(invite: StudioInvitation, accept: boolean) {
    if (accept) {
      await api.acceptStudioInvitation(invite.id);
      window.location.assign('/');
      return;
    }
    await api.declineStudioInvitation(invite.id);
    if (!live.current) return;
    setStudioInvites(current => current.filter(value => value.id !== invite.id));
    setNotice(`Declined the invitation to ${invite.studioName}.`);
  }

  const unread = items.filter(item => !item.read_at).length;
  const counts = useMemo(() => ({
    reviews: items.filter(item => kindOf(item).group === 'review').length,
    invites: items.filter(item => kindOf(item).group === 'invite').length + studioInvites.length,
  }), [items, studioInvites]);
  const showStudioInvites = studioInvites.length > 0 && (filter === 'all' || filter === 'invites');
  const visible = items.filter(item => filter === 'all' || (filter === 'unread' ? !item.read_at : kindOf(item).group === (filter === 'reviews' ? 'review' : 'invite')));
  const groups = visible.reduce<{ label: string; items: NotificationItem[] }[]>((list, item) => {
    const label = dayGroup(stamp(item.created_at), now);
    const last = list[list.length - 1];
    if (last?.label === label) last.items.push(item); else list.push({ label, items: [item] });
    return list;
  }, []);

  const description = loading ? 'Syncing production updates…' : studioInvites.length ? `${studioInvites.length} studio ${studioInvites.length === 1 ? 'invitation' : 'invitations'} waiting` : unread ? `${unread} unread ${unread === 1 ? 'update' : 'updates'}` : items.length ? 'Everything has been seen' : 'Nothing waiting on you';
  const liveLine = <span className={cx('notif-live', syncing && 'is-syncing', error && 'is-offline')} role="status">
    <i aria-hidden="true" />{error ? 'Sync paused' : syncing ? 'Syncing' : 'Live'}
    {syncedAt && !error && <time dateTime={syncedAt.toISOString()}>{clockOf(syncedAt)}</time>}
  </span>;

  let rowIndex = 0;
  return <Modal open size="md" className="notif-modal" title="Notifications" description={description} onOpenChange={onClose} footer={<div className="notif-footer">
    {liveLine}
    {unread > 0 && <div className="notif-footer-actions">
      <Button variant="ghost" icon="send" disabled={busy || loading} onClick={() => void run(async () => { await api.queueNotificationDigest(); if (live.current) { setNotice('Email digest queued.'); void load(true); } })}>Email digest</Button>
      <Button icon="check" disabled={busy || loading} onClick={() => void run(async () => { await api.readAllNotifications(); if (live.current) { edits.current += 1; setItems(current => current.map(item => ({ ...item, read_at: item.read_at || new Date().toISOString() }))); setNotice('All notifications marked as read.'); } })}>Mark all as read</Button>
    </div>}
  </div>}><div className="studio-domain-panel notif-panel" aria-busy={loading || busy}>
    {error && <Alert tone="danger" title="Could not update notifications" action={<Button size="sm" disabled={busy || loading} onClick={() => void load(false)}>Retry</Button>}>{error}</Alert>}
    {notice && <div className="notif-notice" role="status"><Icon name="check" size={14} />{notice}</div>}

    {!loading && items.length + studioInvites.length > 0 && <div className="notif-filters">
      <SegmentedControl label="Filter notifications" value={filter} onChange={value => setFilter(value as Filter)} items={[
        { value: 'all', label: `All ${items.length + studioInvites.length}` },
        { value: 'unread', label: `Unread ${unread}`, disabled: !unread && filter !== 'unread' },
        { value: 'reviews', label: `Reviews ${counts.reviews}`, disabled: !counts.reviews && filter !== 'reviews' },
        { value: 'invites', label: `Invites ${counts.invites}`, disabled: !counts.invites && filter !== 'invites' },
      ]} />
    </div>}

    {loading && <SkeletonRows />}

    {!loading && showStudioInvites && <section className="notif-group" aria-label="Studio invitations">
      <h4 className="notif-group-label">Studio invitations<span>{studioInvites.length}</span></h4>
      <ul className="notif-list">
        {studioInvites.map((invite, index) => {
          const created = stamp(invite.createdAt);
          return <li key={invite.id} className="notif-row is-unread is-accent" style={{ '--i': Math.min(index, 10) } as CSSProperties}>
            <span className="notif-glyph" aria-hidden="true"><Icon name="users" size={16} /></span>
            <div className="notif-content">
              <div className="notif-main">
                <span className="notif-head">
                  <strong>{invite.invitedBy ? `${invite.invitedBy} invited you to ${invite.studioName}` : `You're invited to ${invite.studioName}`}</strong>
                  <time dateTime={created?.toISOString()} title={created?.toLocaleString()}>{relative(created, now)}</time>
                </span>
                <span className="notif-body">Join as {invite.role}. You keep your own studio and can switch between them from the account menu.</span>
                <span className="notif-meta"><span className="notif-kind">Studio invite</span></span>
              </div>
              <div className="notif-actions">
                <Button size="sm" variant="primary" icon="check" disabled={busy} onClick={() => void run(() => answerStudio(invite, true))}>Accept</Button>
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => void run(() => answerStudio(invite, false))}>Decline</Button>
              </div>
            </div>
            <span className="notif-dot" aria-hidden="true" />
          </li>;
        })}
      </ul>
    </section>}

    {!loading && !error && !items.length && !studioInvites.length && <div className="notif-empty">
      <CaughtUpArt />
      <p className="notif-empty-eyebrow"><span aria-hidden="true" />Queue clear<span className="notif-empty-tc"><Timecode /></span></p>
      <div className="notif-empty-text">
        <h3>You're caught up</h3>
        <p className="notif-empty-copy">Reviewer notes, director decisions, invites and assignments land here the moment they happen.</p>
      </div>
    </div>}

    {!loading && items.length > 0 && !visible.length && !showStudioInvites && <div className="notif-empty is-compact">
      <CaughtUpArt compact />
      <h3>{filter === 'unread' ? 'Nothing unread' : `No ${filter} yet`}</h3>
      <Button variant="ghost" size="sm" onClick={() => setFilter('all')}>Show all updates</Button>
    </div>}

    {!loading && groups.map(group => <section key={group.label} className="notif-group" aria-label={group.label}>
      <h4 className="notif-group-label">{group.label}<span>{group.items.length}</span></h4>
      <ul className="notif-list">
        {group.items.map(item => {
          const kind = kindOf(item);
          const created = stamp(item.created_at);
          const invitePayload = item.payload as { invitationId?: unknown; responded?: unknown } | undefined;
          const isInvite = kind.group === 'invite' && typeof invitePayload?.invitationId === 'string';
          const responded = typeof invitePayload?.responded === 'string' ? invitePayload.responded : null;
          const target = presentationOf(item);
          const index = Math.min(rowIndex++, 10);
          return <li key={item.id} className={cx('notif-row', !item.read_at && 'is-unread', fresh.has(item.id) && 'is-fresh', `is-${kind.tone}`)} style={{ '--i': index } as CSSProperties}>
            <span className="notif-glyph" aria-hidden="true"><Icon name={kind.icon} size={16} /></span>
            <div className="notif-content">
              {isInvite && !responded ? <div className="notif-main">
                <span className="notif-head">
                  <strong>{item.title || 'Production update'}</strong>
                  <time dateTime={created?.toISOString()} title={created?.toLocaleString()}>{relative(created, now)}</time>
                </span>
                {item.body && <span className="notif-body">{item.body}</span>}
                <span className="notif-meta">
                  <span className="notif-kind">{kind.label}</span>
                  {target && <span className="notif-open">Open review<Icon name="right" size={12} /></span>}
                  {isInvite && responded && <span className={cx('notif-kind', responded === 'accepted' && 'is-done')}>{responded === 'accepted' ? 'Joined' : 'Declined'}</span>}
                </span>
              </div> : <button type="button" className="notif-main" disabled={busy} onClick={() => void run(() => open(item))} aria-label={`${item.title || 'Production update'}${item.read_at ? '' : ', unread'}${target ? ', open review' : ''}`}>
                <span className="notif-head">
                  <strong>{item.title || 'Production update'}</strong>
                  <time dateTime={created?.toISOString()} title={created?.toLocaleString()}>{relative(created, now)}</time>
                </span>
                {item.body && <span className="notif-body">{item.body}</span>}
                <span className="notif-meta">
                  <span className="notif-kind">{kind.label}</span>
                  {target && <span className="notif-open">Open review<Icon name="right" size={12} /></span>}
                  {isInvite && responded && <span className={cx('notif-kind', responded === 'accepted' && 'is-done')}>{responded === 'accepted' ? 'Joined' : 'Declined'}</span>}
                </span>
              </button>}
              {isInvite && !responded && <div className="notif-actions">
                <Button size="sm" variant="primary" icon="check" disabled={busy} onClick={() => void run(() => respond(item, true))}>Accept</Button>
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => void run(() => respond(item, false))}>Decline</Button>
              </div>}
            </div>
            {!item.read_at && !isInvite && <IconButton className="notif-read" icon="check" label="Mark as read" variant="ghost" size="sm" disabled={busy} onClick={() => void run(() => markRead(item))} />}
            <span className="notif-dot" aria-hidden="true" />
          </li>;
        })}
      </ul>
    </section>)}

    {deliveryError && <Alert tone="warning" title="Email delivery history unavailable">{deliveryError}</Alert>}
    {!!deliveries.length && <details className="notif-deliveries">
      <summary><Icon name="send" size={14} />Email delivery<span>{Math.min(5, deliveries.length)} recent</span><Icon name="down" size={14} className="notif-chevron" /></summary>
      <ul>{deliveries.slice(0, 5).map(item => <li key={item.id} className={`is-${item.status}`}>
        <i aria-hidden="true" /><span>{item.template.replaceAll('-', ' ')}</span>
        <em>{item.status}</em>
        <time>Attempt {item.attempts}{item.sent_at && stamp(item.sent_at) ? ` · ${relative(stamp(item.sent_at), now)}` : ''}</time>
      </li>)}</ul>
    </details>}
  </div></Modal>;
}
